/**
 * What a clinic sees when the plan gate refuses — design/PROTOTYPE-SPEC.md §2,
 * "locked pages show an upgrade checkout".
 *
 * ## Why the upgrade lives here rather than on the Account page
 *
 * The first version of this said "Upgrading is on the Account page" and stopped.
 * That is a dead end dressed as help: someone who navigated to Promotions
 * because they wanted Promotions now has to find a different screen, work out
 * which plan they need, and pick it — three chances to give up, at the exact
 * moment they were most interested. The upgrade belongs where the interest is.
 *
 * ## It sells the feature before it asks for money
 *
 * A lock that only says "needs Pro" tells someone what they cannot have and
 * nothing about whether it is worth having. Each caller passes a short,
 * concrete description of what the plan actually does for them.
 *
 * ## Only the owner sees a button
 *
 * `manageBilling` is owner-only because it spends the clinic's money, and the
 * API refuses regardless. A manager gets told who to ask rather than a button
 * that will 403 — being refused after clicking reads as a broken app.
 */
import {
  PLAN_LABEL,
  PRICING,
  can,
  yearlySaving,
  type Plan,
  type Role as CoreRole,
} from '@inventory-tracker/core';
import { useState } from 'react';
import { startCheckout, type Cycle } from '../lib/api.js';
import { c, primaryButton, sans } from '../theme.js';
import { cardStyle, money, smallButton } from './format.js';

interface Props {
  /** From the API's 402 — never re-derived on this side. */
  requiredPlan: Plan;
  /**
   * The clinic's current cycle. It is the starting choice, so an upgrade keeps
   * their billing rhythm unless they pick the other one here.
   */
  cycle: Cycle;
  role: CoreRole;
  title: string;
  /** One or two sentences on what this actually does. Sells, then asks. */
  blurb: string;
}

export function PlanLocked({ requiredPlan, cycle: currentCycle, role, title, blurb }: Props) {
  const [cycle, setCycle] = useState<Cycle>(currentCycle);
  const price = PRICING[requiredPlan];
  const mayBuy = can(role, 'manageBilling');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function upgrade() {
    setBusy(true);
    setError(null);
    try {
      const { url } = await startCheckout(requiredPlan, cycle);
      window.location.assign(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start checkout');
      setBusy(false);
    }
  }

  return (
    <div style={{ ...cardStyle, maxWidth: 620, margin: '0 auto' }}>
      <span
        style={{
          display: 'inline-block',
          border: `1px solid ${c.border}`,
          color: c.green,
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: '0.7px',
          borderRadius: 980,
          padding: '3px 9px',
          marginBottom: 12,
        }}
      >
        {PLAN_LABEL[requiredPlan].toUpperCase()}
      </span>

      <h2 style={{ fontFamily: sans, letterSpacing: '-0.03em', fontWeight: 300, fontSize: 24, margin: '0 0 10px' }}>
        {title}
      </h2>
      <p style={{ fontSize: 14, color: c.muted, lineHeight: 1.75, margin: '0 0 18px' }}>{blurb}</p>

      {error && (
        <div
          role="alert"
          style={{
            background: c.dangerBg,
            border: `1px solid ${c.dangerBorder}`,
            borderRadius: 12,
            padding: '10px 14px',
            marginBottom: 14,
            fontSize: 13,
            color: c.danger,
          }}
        >
          {error}
        </div>
      )}

      {mayBuy ? (
        <>
        {/* Monthly or yearly is asked here too. It used to be decided silently
            from the current cycle, with the choice only on the Account page. */}
        <div
          role="radiogroup"
          aria-label="Billing"
          style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}
        >
          {(['monthly', 'yearly'] as Cycle[]).map((k) => {
            const on = cycle === k;
            return (
              <button
                key={k}
                type="button"
                role="radio"
                aria-checked={on}
                disabled={busy}
                onClick={() => setCycle(k)}
                style={{
                  textAlign: 'left',
                  fontFamily: sans,
                  cursor: 'pointer',
                  background: c.white,
                  border: `1px solid ${on ? c.green : c.borderStrong}`,
                  boxShadow: on ? `0 0 0 1px ${c.green}` : 'none',
                  borderRadius: 14,
                  padding: '11px 14px',
                  color: c.ink,
                }}
              >
                <div style={{ fontSize: 14, fontWeight: 500 }}>
                  {k === 'monthly' ? 'Monthly' : 'Yearly'}
                </div>
                <div style={{ fontSize: 13, color: c.muted, marginTop: 2 }}>
                  {k === 'monthly'
                    ? `${money(price.monthly)} a month`
                    : `${money(price.yearlyTotal)} a year, save ${money(yearlySaving(requiredPlan))}`}
                </div>
              </button>
            );
          })}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button
            type="button"
            disabled={busy}
            onClick={() => void upgrade()}
            style={{
              ...primaryButton,
              width: 'auto',
              height: 44,
              padding: '0 22px',
              opacity: busy ? 0.6 : 1,
            }}
          >
            {busy ? 'Opening…' : `Upgrade to ${PLAN_LABEL[requiredPlan]}`}
          </button>
          <span style={{ fontSize: 12.5, color: c.muted2, fontFamily: sans }}>
            Billed {cycle}. Cancel anytime.
          </span>
        </div>
        </>
      ) : (
        <p style={{ fontSize: 13.5, color: c.muted2, lineHeight: 1.7, margin: 0 }}>
          Ask the clinic owner to upgrade. Only they can change the plan.
        </p>
      )}

      <p style={{ fontSize: 12, color: c.faint, margin: '16px 0 0', lineHeight: 1.6 }}>
        Compare every plan on the{' '}
        <span style={{ ...smallButton, border: 'none', padding: 0, background: 'none' }}>
          Account
        </span>{' '}
        page.
      </p>
    </div>
  );
}
